import React, { useState, useEffect, useMemo } from 'react';
import { useTranslations } from 'use-intl';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Loader2, Receipt, MoreHorizontal, CreditCard, Banknote } from 'lucide-react';
import { format } from 'date-fns';
import { ExpenseReimbursement, ExpenseStatus } from '@/types/expense';
import { Invoice } from '@/types/payments';
import { EXPENSE_CATEGORY_INFO } from '@/constants/expenseCategories';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { toast } from 'sonner';
import ExpenseReviewScreen from './ExpenseReviewScreen';
import MarkPaidExternallyModal from './MarkPaidExternallyModal';
import ConfirmPaymentModal from '@/components/payments/confirmation/ConfirmPaymentModal';
import SekEquivalent from '@/components/shared/SekEquivalent';
import { formatCurrency } from '@/utils/paymentUtils';
import { expenseToPaymentInvoice, useExpenseStatus } from './expensePaymentInvoice';

type TabValue = 'action_needed' | 'awaiting_payment' | 'under_review' | 'scheduled' | 'completed' | 'rejected' | 'all';

const TAB_FILTERS: Record<TabValue, ExpenseStatus[] | null> = {
  action_needed: ['awaiting_payment_approval'],
  awaiting_payment: ['payment_queued'],
  under_review: ['suggestion_ready'],
  scheduled: ['payment_initiated'],
  completed: ['paid', 'closed'],
  rejected: ['rejected'],
  all: null, // show all except draft
};

const ExpenseListPage: React.FC = () => {
  const t = useTranslations('consultantExpenses');
  const expenseStatus = useExpenseStatus();
  const [expenses, setExpenses] = useState<ExpenseReimbursement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpenseId, setSelectedExpenseId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabValue>('action_needed');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentInvoices, setPaymentInvoices] = useState<Invoice[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [markPaidExpense, setMarkPaidExpense] = useState<ExpenseReimbursement | null>(null);

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const data = await ServiceRegistry.getExpenseService().listExpenses();
      setExpenses(data.slice().sort((a, b) => b.created_at.localeCompare(a.created_at)));
    } catch (error) {
      toast.error(t('list.toasts.loadFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, []);

  // Only show expenses that have a suggestion created (suggestion_ready or later)
  const VISIBLE_STATUSES: ExpenseStatus[] = [
    'suggestion_ready', 'awaiting_payment_approval', 'payment_queued',
    'payment_initiated', 'paid', 'closed', 'rejected'
  ];
  const visibleExpenses = useMemo(() =>
    expenses.filter(e => VISIBLE_STATUSES.includes(e.status)),
    [expenses]
  );

  const filteredExpenses = useMemo(() => {
    const statuses = TAB_FILTERS[activeTab];
    if (!statuses) return visibleExpenses;
    return visibleExpenses.filter(e => statuses.includes(e.status));
  }, [visibleExpenses, activeTab]);

  const selectableExpenses = useMemo(() =>
    filteredExpenses.filter(e => e.status === 'awaiting_payment_approval'),
    [filteredExpenses]
  );

  const isUnderReview = (status: ExpenseStatus) =>
    status === 'suggestion_ready';

  const formatCategory = (category: string | null) => {
    if (!category) return '—';
    return EXPENSE_CATEGORY_INFO[category as keyof typeof EXPENSE_CATEGORY_INFO]?.label || category;
  };

  // Selection handlers
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === selectableExpenses.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableExpenses.map(e => e.id)));
    }
  };

  const mapToInvoice = (
    exp: ExpenseReimbursement,
    payable: { id: string; payment_date?: string; source_account_id?: string | null },
  ): Invoice =>
    expenseToPaymentInvoice(exp, {
      payableId: payable.id,
      paymentDate: payable.payment_date,
      sourceAccountId: payable.source_account_id,
      fallbackVendor: t('employeeFallback'),
    });

  // Bulk action handlers
  const validateSelection = (ids: string[]): boolean => {
    const missing = ids
      .map(id => filteredExpenses.find(e => e.id === id))
      .filter(e => e && (!e.employee_id || !e.employee_iban));
    if (missing.length > 0) {
      toast.error(t('list.toasts.missingEmployeeOrIban', { count: String(missing.length) }));
      return false;
    }
    return true;
  };

  const handleBulkApproveAndPay = async () => {
    const ids = Array.from(selectedIds);
    if (!validateSelection(ids)) return;

    try {
      setBulkActionLoading(true);
      const result = await ServiceRegistry.getExpenseService().bulkApproveAndQueuePayment(ids);

      if (result.failures.length > 0) {
        toast.error(t('list.toasts.approveFailed', { count: String(result.failures.length) }));
      }

      if (result.successes.length > 0) {
        const invoices: Invoice[] = result.successes.map(({ expense, payable }) =>
          mapToInvoice(expense, payable)
        );
        setPaymentInvoices(invoices);
        setPaymentModalOpen(true);
      }

      setSelectedIds(new Set());
      loadExpenses();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkMarkPaidExternally = async () => {
    const ids = Array.from(selectedIds);
    if (!validateSelection(ids)) return;

    try {
      setBulkActionLoading(true);
      const paidDate = format(new Date(), 'yyyy-MM-dd');
      const result = await ServiceRegistry.getExpenseService().bulkApproveAndMarkPaidExternally(ids, paidDate);
      if (result.failures.length > 0) {
        toast.error(t('list.toasts.failed', { count: String(result.failures.length) }));
      }
      if (result.successes.length > 0) {
        toast.success(t('list.toasts.markedPaid', { count: String(result.successes.length) }));
      }
      setSelectedIds(new Set());
      loadExpenses();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Row-level action handlers
  const handleRowApproveAndPay = async (expense: ExpenseReimbursement) => {
    try {
      setBulkActionLoading(true);
      const { expense: updated, payable } = await ServiceRegistry.getExpenseService().approveAndQueuePayment(expense.id);
      setPaymentInvoices([mapToInvoice(updated, payable)]);
      setPaymentModalOpen(true);
      loadExpenses();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleRowMarkPaidExternally = (expense: ExpenseReimbursement) => {
    setMarkPaidExpense(expense);
  };

  const handleMarkPaidExternallyComplete = () => {
    setMarkPaidExpense(null);
    loadExpenses();
  };

  if (selectedExpenseId) {
    return <ExpenseReviewScreen expenseId={selectedExpenseId} onBack={() => { setSelectedExpenseId(null); loadExpenses(); }} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('list.title')}</h1>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as TabValue); setSelectedIds(new Set()); }}>
        <TabsList className="w-full justify-start mb-4">
          <TabsTrigger value="action_needed">{t('list.tabs.action_needed')}</TabsTrigger>
          <TabsTrigger value="under_review">{t('list.tabs.under_review')}</TabsTrigger>
          <TabsTrigger value="awaiting_payment">{t('list.tabs.awaiting_payment')}</TabsTrigger>
          <TabsTrigger value="scheduled">{t('list.tabs.scheduled')}</TabsTrigger>
          <TabsTrigger value="completed">{t('list.tabs.completed')}</TabsTrigger>
          <TabsTrigger value="rejected">{t('list.tabs.rejected')}</TabsTrigger>
          <TabsTrigger value="all">{t('list.tabs.all')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex gap-2 items-center p-2 bg-muted rounded-lg">
          <span className="text-sm font-medium">{t('list.selected', { count: String(selectedIds.size) })}</span>
          <Button size="sm" onClick={handleBulkApproveAndPay} disabled={bulkActionLoading}>
            <CreditCard className="mr-2 h-4 w-4" />
            {t('list.approvePayCount', { count: String(selectedIds.size) })}
          </Button>
          <Button size="sm" variant="outline" onClick={handleBulkMarkPaidExternally} disabled={bulkActionLoading}>
            <Banknote className="mr-2 h-4 w-4" />
            {t('list.approvePayExternallyCount', { count: String(selectedIds.size) })}
          </Button>
        </div>
      )}

      {filteredExpenses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Receipt className="h-12 w-12 mb-4" />
          <p>
            {t(visibleExpenses.length === 0 ? 'list.emptyNone' : 'list.emptyCategory')}
          </p>
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  {selectableExpenses.length > 0 && (
                    <Checkbox
                      checked={selectedIds.size === selectableExpenses.length && selectableExpenses.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  )}
                </TableHead>
                <TableHead>{t('list.columns.vendor')}</TableHead>
                <TableHead>{t('list.columns.employee')}</TableHead>
                <TableHead>{t('list.columns.category')}</TableHead>
                <TableHead>{t('list.columns.date')}</TableHead>
                <TableHead>{t('list.columns.amount')}</TableHead>
                <TableHead>{t('list.columns.status')}</TableHead>
                <TableHead>{t('list.columns.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredExpenses.map((expense) => (
                <TableRow
                  key={expense.id}
                  className={isUnderReview(expense.status) ? 'opacity-60' : ''}
                >
                  <TableCell>
                    {expense.status === 'awaiting_payment_approval' && (
                      <Checkbox
                        checked={selectedIds.has(expense.id)}
                        onCheckedChange={() => toggleSelect(expense.id)}
                      />
                    )}
                  </TableCell>
                  <TableCell>{expense.vendor || '—'}</TableCell>
                  <TableCell>{expense.employee_name || '—'}</TableCell>
                  <TableCell>{formatCategory(expense.expense_category)}</TableCell>
                  <TableCell>{expense.expense_date || '—'}</TableCell>
                  <TableCell>
                    {expense.amount ? (
                      <>
                        {formatCurrency(expense.amount, expense.currency || 'SEK')}
                        <SekEquivalent sekAmount={expense.sek_amount} currency={expense.currency || 'SEK'} />
                      </>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={expenseStatus(expense.status).variant}>
                      {expenseStatus(expense.status).label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setSelectedExpenseId(expense.id)}>
                        {t('list.view')}
                      </Button>
                      {expense.status === 'awaiting_payment_approval' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleRowApproveAndPay(expense)}
                              disabled={!expense.employee_id || !expense.employee_iban}
                            >
                              <CreditCard className="mr-2 h-4 w-4" />{t('actions.approvePay')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleRowMarkPaidExternally(expense)}
                              disabled={!expense.employee_id || !expense.employee_iban}
                            >
                              <Banknote className="mr-2 h-4 w-4" />{t('actions.approvePayExternally')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {expense.status === 'payment_queued' && expense.payable_id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleRowApproveAndPay(expense)}>
                              <CreditCard className="mr-2 h-4 w-4" />{t('actions.pay')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleRowMarkPaidExternally(expense)}>
                              <Banknote className="mr-2 h-4 w-4" />{t('actions.markPaidExternally')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmPaymentModal
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        selectedInvoices={paymentInvoices}
        actionInvoiceId={null}
        onConfirmPayment={() => { loadExpenses(); }}
        refreshData={async () => { await loadExpenses(); }}
        autoStartSigning
      />

      {markPaidExpense && (
        <MarkPaidExternallyModal
          open={!!markPaidExpense}
          onOpenChange={(nextOpen) => { if (!nextOpen) setMarkPaidExpense(null); }}
          expense={markPaidExpense}
          onComplete={handleMarkPaidExternallyComplete}
        />
      )}
    </div>
  );
};

export default ExpenseListPage;
